// app.js — SPA router

import { encrypt, decrypt } from './crypto.js';
import { createMessage, getMessage, burnMessage, deleteMessage,
         getMe, requestMagicLink, registerAccount, logout, deleteAccount,
         requestUpgrade } from './api.js';

const app = document.getElementById('app');

// ── Theme toggle ─────────────────────────────────────────────────────────────

function initTheme() {
  const toggle = document.getElementById('themeToggle');
  if (!toggle) return;

  // Only the icon span is swapped: writing textContent on the button would
  // wipe the label shown beside it in the open mobile menu.
  const icon = toggle.querySelector('[aria-hidden="true"]') ?? toggle;
  const label = toggle.querySelector('.theme-label');

  const update = (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    icon.textContent = theme === 'dark' ? '☀️' : '🌙';
    const text = theme === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro';
    toggle.title = text;
    toggle.setAttribute('aria-label', text);
    if (label) label.textContent = text;
  };

  // Sync icon with current theme on load
  update(document.documentElement.getAttribute('data-theme') || 'light');

  toggle.addEventListener('click', () => {
    const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    localStorage.setItem('theme', next);
    update(next);
  });
}

// ── Burger menu ───────────────────────────────────────────────────────────────

function initBurger() {
  const burger = document.getElementById('navBurger');
  const links  = document.getElementById('navLinks');
  if (!burger || !links) return;
  const setOpen = open => {
    links.classList.toggle('open', open);
    burger.setAttribute('aria-expanded', String(open));
  };
  burger.setAttribute('aria-expanded', 'false');
  burger.setAttribute('aria-controls', 'navLinks');
  burger.addEventListener('click', () => setOpen(!links.classList.contains('open')));
  // SPA navigation swaps the page without a reload, so the panel would stay
  // open on top of the content the user just asked for.
  links.addEventListener('click', e => {
    if (e.target.closest('a')) setOpen(false);
  });
}

initTheme();
initBurger();

// The longer options stay visible but disabled for whoever cannot use them:
// they advertise the paid plans better than hiding them would.
// These ceilings mirror the server's, which is the one that counts — the
// select is a hint, and a POST can ask for anything.
const FREE_MAX_TTL = 86400;
const PRO_MAX_TTL = 604800;
const TTL_OPTIONS = [
  { label: '1 ora',    value: 3600 },
  { label: '6 ore',    value: 21600 },
  { label: '24 ore',   value: 86400 },
  { label: '3 giorni', value: 259200 },
  { label: '7 giorni', value: 604800 },
];

// ── Session ───────────────────────────────────────────────────────────────────
// Read once at boot. Null means logged out, which is also what a failed call
// means: the site has to work regardless.
let session = null;

function ttlCeiling() {
  return session?.plan === 'pro' ? PRO_MAX_TTL : FREE_MAX_TTL;
}

function renderNavSession() {
  const slot = document.getElementById('navSession');
  if (!slot) return;
  slot.innerHTML = session
    ? `<span class="nav-account" title="${escapeHtml(session.email)}">${escapeHtml(session.email)}</span>`
      + `<button class="btn-link" id="logoutBtn">Esci</button>`
    : `<a href="/accedi" class="nav-hide-mobile">Accedi</a>`;

  document.getElementById('logoutBtn')?.addEventListener('click', async () => {
    await logout();
    session = null;
    renderNavSession();
    route();
  });
}

// One independent single-use link per recipient — NOT several reads of one
// link, which would make an interception invisible. See specs/multi-recipient.
const MAX_RECIPIENTS_PRO = 5;

function maxRecipients() {
  return session?.plan === 'pro' ? MAX_RECIPIENTS_PRO : 1;
}

const INTERSTITIAL_SECONDS = 5;

// ── Helpers ──────────────────────────────────────────────────────────────────

function footer() {
  return `<footer>
    <span class="footer-claim">Messaggi effimeri · Cifrati sul tuo dispositivo · Server in Europa</span>
    <br>&copy; ${new Date().getFullYear()} solounavolta.it &mdash;
    <a href="/verifica">Verificalo tu stesso</a> · <a href="/privacy">Privacy</a> ·
    <a href="/termini">Termini</a> ·
    <a href="/contatti">Contatti</a> · <a href="/sponsorizzazioni">Sponsorizzazioni</a>
    <br><span style="opacity:0.75">Monotask S.r.l. · P.IVA 08278810729 · Altamura (BA), Italia</span>
  </footer>`;
}

// Page heading for the app screens. The brand is already in the fixed navbar,
// so repeating it in the body was saying the same thing twice; what those
// screens actually lacked was a heading describing the page.
function pageTitle(text, sub = '') {
  return `<div class="page-head">
    <h1>${text}</h1>
    ${sub ? `<p>${sub}</p>` : ''}
  </div>`;
}

function render(html) {
  // Reset the full-bleed layout: only the interstitial re-enables it, right
  // after rendering. Otherwise it would leak into the next page.
  app.classList.remove('stage-mode');
  app.innerHTML = html + footer();
}

// `input` is the field to select when the clipboard refuses, so the user can
// still copy by hand. Passed in rather than looked up by id: with one block
// per recipient there is no longer a single #linkInput to find.
function copyToClipboard(text, btn, restoreLabel = 'Copia', input = null) {
  const done = () => {
    btn.textContent = '✓ Copiato';
    setTimeout(() => (btn.textContent = restoreLabel), 2000);
  };
  const failed = () => {
    // navigator.clipboard is undefined outside a secure context and can reject
    // when the browser does not consider this a user gesture. Silence here made
    // the user paste whatever was in the clipboard before, believing it worked.
    btn.textContent = '✕ Copia non riuscita';
    if (input) { input.focus(); input.select(); }
    setTimeout(() => (btn.textContent = restoreLabel), 4000);
  };
  try {
    navigator.clipboard.writeText(text).then(done, failed);
  } catch {
    failed();
  }
}

// ── Pages ─────────────────────────────────────────────────────────────────────

function pageCreate() {
  const ceiling = ttlCeiling();
  const ttlOpts = TTL_OPTIONS.map(o => {
    const locked = o.value > ceiling;
    return `<option value="${o.value}"${locked ? ' disabled' : ''}${o.value === FREE_MAX_TTL ? ' selected' : ''}>`
      + `${o.label}${locked ? ' — piani a pagamento' : ''}</option>`;
  }).join('');

  // Each recipient gets their own link, still good for exactly one read.
  const maxTo = maxRecipients();
  const recipientOpts = Array.from({ length: MAX_RECIPIENTS_PRO }, (_, i) => i + 1)
    .map(n => {
      const locked = n > maxTo;
      return `<option value="${n}"${locked ? ' disabled' : ''}${n === 1 ? ' selected' : ''}>`
        + `${n} ${n === 1 ? 'destinatario' : 'destinatari'}`
        + `${locked ? ' — piani a pagamento' : ''}</option>`;
    }).join('');

  render(`
    ${pageTitle('Crea un messaggio', 'Si distrugge dopo la prima lettura')}
    <div class="card">
      <label for="msg">Il tuo messaggio</label>
      <textarea id="msg" placeholder="Scrivi il tuo messaggio segreto…" maxlength="49000"></textarea>
      <div class="form-row">
        <label for="ttl" class="sr-only">Il link scade dopo</label>
        <select id="ttl" aria-label="Il link scade dopo">${ttlOpts}</select>
        <label for="copies" class="sr-only">Quanti destinatari</label>
        <select id="copies" aria-label="Quanti destinatari">${recipientOpts}</select>
        <button class="btn btn-primary" id="sendBtn">🔒 Cifra e genera link</button>
      </div>
      <div class="notice notice-warn" id="createError" role="alert" hidden style="margin-top:1.2rem"></div>
      <div class="notice notice-info" style="margin-top:1.2rem">
        🔐 Il messaggio viene cifrato nel tuo browser. Il server non vede mai il contenuto.
      </div>
    </div>
  `);

  document.getElementById('sendBtn').addEventListener('click', async () => {
    const text = document.getElementById('msg').value.trim();
    if (!text) return;
    const btn = document.getElementById('sendBtn');
    btn.disabled = true;
    btn.textContent = '⏳ Cifratura…';
    try {
      const ttl = parseInt(document.getElementById('ttl').value, 10);
      const copies = parseInt(document.getElementById('copies').value, 10) || 1;
      // Encrypted once: every recipient gets the same key in the fragment,
      // but a token of their own.
      const { ciphertext, key } = await encrypt(text);
      const res = await createMessage(ciphertext, ttl, copies);
      // The marker rides in the query, never in the fragment: the fragment is
      // the decryption key and its format has to keep working for links that
      // are already out there.
      const mark = res.no_ads ? '?np=1' : '';
      pageSent(res.links.map(l => ({
        link: `${location.origin}/m/${l.token}${mark}#${key}`,
        deleteToken: l.delete_token,
      })));
    } catch (e) {
      btn.disabled = false;
      btn.textContent = '🔒 Cifra e genera link';
      // Inline, not alert(): a system dialog showing the domain looks like a
      // browser warning, which on a security product reads as phishing.
      const box = document.getElementById('createError');
      if (box) {
        if (e.status === 429) {
          // The moment someone hits the cap is the moment they have just proved
          // the product is useful to them — so offer the way out here, not a
          // dead end. Static markup: never interpolate a message as HTML.
          box.innerHTML = 'Hai finito i messaggi gratuiti di oggi. '
            + 'Il tuo testo è ancora qui: riprova domani, oppure '
            + '<a href="/pricing">passa a un piano senza limiti</a>.';
        } else {
          box.textContent = e.message;
        }
        box.hidden = false;
        box.scrollIntoView({ block: 'nearest' });
      }
    }
  });
}

function pageSent(sent) {
  // Survive a refresh. This screen used to live only in a closure: a
  // pull-to-refresh — a common accidental gesture on the mobile-first flow this
  // product is built around — threw away both the link and the delete_token,
  // and with the delete_token goes the only way to revoke a message sent to the
  // wrong person. sessionStorage keeps it to this tab and clears on close.
  // The whole array is saved: keeping only the first would put the bug back
  // for every recipient but one.
  try {
    sessionStorage.setItem('lastSent', JSON.stringify({ sent }));
  } catch { /* private mode: the page still works, it just will not survive */ }
  history.replaceState({ sent }, '', '/inviato');

  const many = sent.length > 1;
  const rows = sent.map((s, i) => `
    <div class="link-row" data-i="${i}">
      ${many ? `<span class="link-label">Destinatario ${i + 1}</span>` : ''}
      <div class="link-box">
        <input id="linkInput${i}" type="text" readonly value="${s.link}" />
        <button class="btn btn-primary" data-copy="${i}">Copia</button>
      </div>
      <div class="revoke-box" data-revokebox="${i}">
        <button class="btn btn-danger" data-revoke="${i}">🗑️ Revoca</button>
        <span class="revoke-status" data-status="${i}"></span>
      </div>
    </div>
  `).join('');

  render(`
    ${pageTitle(many ? `${sent.length} link generati` : 'Link generato',
                many ? 'Uno per destinatario, da consegnare separatamente'
                     : 'Condividilo con il destinatario')}
    <div class="card">
      ${rows}
      <div class="notice notice-warn" style="margin-top:1.2rem">
        ⚠️ ${many
          ? 'Ogni link funziona <strong>una sola volta</strong>, indipendentemente dagli altri. Non mandare lo stesso link a due persone: la seconda lo troverebbe già distrutto.'
          : 'Questo link funziona <strong>una sola volta</strong>. Dopo la prima apertura il messaggio viene distrutto.'}
      </div>
    </div>
    <p class="secondary-cta">
      <a href="/app">Crea un altro messaggio</a>
    </p>
  `);

  app.querySelectorAll('[data-copy]').forEach(btn =>
    btn.addEventListener('click', e => {
      const i = Number(e.currentTarget.dataset.copy);
      copyToClipboard(sent[i].link, e.currentTarget, 'Copia', document.getElementById(`linkInput${i}`));
    })
  );

  app.querySelectorAll('[data-revoke]').forEach(btn =>
    btn.addEventListener('click', e => revokeOne(sent, Number(e.currentTarget.dataset.revoke), e.currentTarget))
  );
}

async function revokeOne(sent, i, btn) {
  const { link, deleteToken } = sent[i];
  const status = app.querySelector(`[data-status="${i}"]`);
  const box = app.querySelector(`[data-revokebox="${i}"]`);
  const fail = (text) => {
    status.textContent = text;
    status.className = 'revoke-status warn';
    btn.disabled = false;
    btn.textContent = '🗑️ Revoca';
  };

  if (!confirm('Revocare il link? Il messaggio non potrà più essere letto.')) return;
  btn.disabled = true;
  btn.textContent = '⏳ Revoca…';
  try {
    const res = await deleteMessage(tokenFromLink(link), deleteToken);
    if (res.ok) {
      status.textContent = '✓ Link revocato';
      status.className = 'revoke-status ok';
      box.classList.add('done');
      btn.remove();
    } else if (res.error === 'not_found') {
      status.textContent = 'Il messaggio è già stato letto o è scaduto.';
      status.className = 'revoke-status warn';
      btn.remove();
    } else {
      fail('Errore di autorizzazione.');
    }
  } catch {
    fail('Errore di rete. Riprova.');
  }
}

function tokenFromLink(link) {
  const m = link.match(/\/m\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : '';
}

// ── Sponsor rotation ─────────────────────────────────────────────────────────
// Sponsors are declared in /sponsor/sponsors.json and served from our own
// origin. Deliberately NOT a third-party ad script: on /m/ the token is in the
// path and the key in the fragment, so any foreign script here could read both.
// Adding a sponsor is a JSON edit plus an image — no code change.

async function pickSponsor() {
  try {
    const res = await fetch('/sponsor/sponsors.json', { cache: 'no-cache' });
    if (!res.ok) return null;
    const { sponsors = [] } = await res.json();
    const now = Date.now();
    const active = sponsors.filter(s =>
      s.image && s.href &&
      (!s.from  || now >= Date.parse(s.from)) &&
      (!s.until || now <= Date.parse(s.until))
    );
    if (!active.length) return null;

    // Weighted pick, so a paying sponsor can buy a larger share of impressions.
    const total = active.reduce((sum, s) => sum + (s.weight ?? 1), 0);
    let r = Math.random() * total;
    for (const s of active) {
      r -= s.weight ?? 1;
      if (r <= 0) return s;
    }
    return active[active.length - 1];
  } catch {
    // Never let the ad slot break the page the reader actually came for.
    return null;
  }
}

async function pageInterstitial(token, key) {
  let secs = INTERSTITIAL_SECONDS;
  const sponsor = await pickSponsor();
  const bg = sponsor
    ? `<a class="stage-bg" href="${sponsor.href}" rel="noopener nofollow sponsored"
          target="_blank" style="background-image:url('${sponsor.image}')"
          aria-label="Contenuto sponsorizzato: ${sponsor.label}"></a>`
    : `<div class="stage-bg stage-bg-default" aria-hidden="true"></div>`;

  render(`
    <div class="stage">
      ${bg}
      <aside class="stage-panel">
        <h1>Stai per leggere un messaggio segreto</h1>
        <p>Potrai leggerlo <strong>una sola volta</strong>. Dopo sarà distrutto
        definitivamente: assicurati di poterlo copiare adesso.</p>
        <div class="countdown" id="cd">${secs}</div>
        <button class="btn btn-primary" id="readBtn" disabled>
          🔓 Leggi il messaggio
        </button>
        ${sponsor ? `<p class="stage-sponsor">Sponsorizzato da ${sponsor.label}</p>` : ''}
      </aside>
    </div>
  `);

  app.classList.add('stage-mode');

  const cdEl = document.getElementById('cd');
  const readBtn = document.getElementById('readBtn');
  const timer = setInterval(() => {
    secs--;
    cdEl.textContent = secs;
    if (secs <= 0) {
      clearInterval(timer);
      cdEl.textContent = '';
      readBtn.disabled = false;
      readBtn.textContent = '🔓 Leggi il messaggio';
    }
  }, 1000);

  readBtn.addEventListener('click', () => pageView(token, key));
}

async function pageView(token, key) {
  render(`<div class="spinner" role="status" aria-label="Caricamento del messaggio"></div>`);
  try {
    const data = await getMessage(token);
    if (!data) {
      pageDestroyed();
      return;
    }
    // Decrypt BEFORE confirming: if this throws, the message is still on the
    // server and the reader can retry with a complete link.
    const plaintext = await decrypt(data.ciphertext, key);
    await burnMessage(token);

    // The key lives in the fragment; once read there is no reason to keep it
    // in the address bar or in browser history (which may sync across devices).
    history.replaceState({}, '', '/letto');

    // One warning, not two: the badge and the notice said the same thing in two
    // colours, competing with the content the reader actually came for.
    render(`
      ${pageTitle('Il tuo messaggio')}
      <div class="card">
        <div class="message-content" id="msgContent">${escapeHtml(plaintext)}</div>
        <div class="read-actions">
          <button class="btn btn-primary" id="copyMsgBtn">Copia messaggio</button>
          <p class="read-warning">
            Questo messaggio è stato consumato: fra pochi secondi questa pagina
            sarà l'unica copia rimasta. <strong>Copialo ora se ti serve.</strong>
          </p>
        </div>
      </div>
      <p class="secondary-cta">
        Devi inviare qualcosa anche tu? <a href="/app">Crea un messaggio</a>
      </p>
    `);
    document.getElementById('copyMsgBtn').addEventListener('click', e =>
      copyToClipboard(plaintext, e.currentTarget, 'Copia messaggio')
    );
    // The secondary CTA is a plain link now, handled by the global SPA click
    // interceptor — no listener needed.
  } catch (e) {
    // The message was NOT burned: decryption threw before the confirmation, so
    // it is still on the server and a complete link will still open it.
    render(`<div class="card">
      <div class="destroyed-notice">
        <div class="icon">❌</div>
        <h2>Non riusciamo a leggere questo messaggio</h2>
        <p>Il link sembra incompleto: probabilmente si è spezzato copiandolo o
        inoltrandolo. Il messaggio <strong>non è andato perso</strong> — chiedi a
        chi te l'ha inviato di rimandarti il link intero e riprova.</p>
      </div>
    </div>`);
  }
}

function pageIncompleteLink() {
  render(`
    <div class="card">
      <div class="destroyed-notice">
        <div class="icon">🔗</div>
        <h2>Questo link è incompleto</h2>
        <p>Manca la parte dopo il simbolo <code>#</code>, che contiene la chiave
        per decifrare il messaggio e non viene mai inviata a noi. Senza quella
        non possiamo aprirlo — e non possiamo recuperarla.</p>
        <p><strong>Il messaggio è ancora al sicuro e non è stato letto.</strong>
        Chiedi a chi te l'ha inviato di rimandarti il link completo.</p>
      </div>
    </div>`);
}

function pageDestroyed() {
  render(`
    <div class="card">
      <div class="destroyed-notice">
        <div class="icon">💨</div>
        <h2>Messaggio non trovato</h2>
        <p>È già stato letto oppure è scaduto.</p>
      </div>
    </div>
    <p class="secondary-cta"><a href="/app">Crea un messaggio</a></p>
  `);
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Marketing pages ──────────────────────────────────────────────────────────

function pageHome() {
  app.classList.add('wide');
  render(`
    <section class="hero">
      <div class="hero-eyebrow">🔐 Cifrato sul tuo dispositivo · Server in Europa · Si distrugge dopo la lettura</div>
      <h1>La password che invii oggi<span> non resterà in chat per sempre</span></h1>
      <p>Condividi credenziali e dati riservati con un link che funziona una sola volta, poi sparisce.<br>Nessuna registrazione, nessuna email.</p>
      <div class="hero-cta">
        <a href="/app" class="btn btn-primary" id="heroCtaBtn">🔒 Crea messaggio gratis</a>
        <a href="/come-funziona" class="btn btn-ghost" id="heroHowBtn">Come funziona →</a>
      </div>
    </section>

    <div class="feature-grid">
      <div class="feature-card"><div class="icon">🔐</div><h3>Non possiamo leggerlo nemmeno noi</h3><p>Il testo viene cifrato sul tuo dispositivo e la chiave non ci arriva mai.<br><small>XChaCha20-Poly1305, lo stesso algoritmo usato da Signal.</small></p></div>
      <div class="feature-card"><div class="icon">💨</div><h3>Si distrugge alla prima lettura</h3><p>Quando il destinatario lo apre, il messaggio viene cancellato dai nostri server.<br><small>Restano pochi secondi per riprovare se la rete cade a metà: serve a non perdere il messaggio.</small></p></div>
      <div class="feature-card"><div class="icon">🇪🇺</div><h3>I tuoi dati non lasciano l'Europa</h3><p>Server a Parigi, azienda europea, nessun trasferimento fuori dall'UE.</p></div>
      <div class="feature-card"><div class="icon">⚡</div><h3>Nessuna registrazione</h3><p>Per inviare un messaggio non serve un account: apri, scrivi, condividi.<br><small>L'account è facoltativo e serve solo se vuoi limiti più alti.</small></p></div>
    </div>

    <div class="cta-banner">
      <h2>Pronto a inviare il tuo primo messaggio segreto?</h2>
      <p>Gratis, nessun account richiesto.</p>
      <a href="/app" class="btn btn-primary" id="ctaBottomBtn">🔒 Inizia ora</a>
    </div>
  `);

  document.getElementById('heroCtaBtn').addEventListener('click', e => { e.preventDefault(); history.pushState({}, '', '/app'); app.classList.remove('wide'); pageCreate(); });
  document.getElementById('heroHowBtn').addEventListener('click', e => { e.preventDefault(); history.pushState({}, '', '/come-funziona'); pageHowItWorks(); });
  document.getElementById('ctaBottomBtn').addEventListener('click', e => { e.preventDefault(); history.pushState({}, '', '/app'); app.classList.remove('wide'); pageCreate(); });
}

function pageHowItWorks() {
  app.classList.add('wide');
  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">Come funziona</div>
      <h1>Tre passi, nessun compromesso<span> sulla privacy</span></h1>
    </section>

    <div class="feature-grid">
      <div class="feature-card"><div class="icon">1️⃣</div><h3>Scrivi il messaggio</h3><p>Il testo viene cifrato nel tuo browser con XChaCha20-Poly1305 prima di lasciare il dispositivo.</p></div>
      <div class="feature-card"><div class="icon">2️⃣</div><h3>Condividi il link</h3><p>Ricevi un link unico. La chiave di cifratura è nel frammento <code>#</code> — mai inviata al server.</p></div>
      <div class="feature-card"><div class="icon">3️⃣</div><h3>Lettura unica</h3><p>Il destinatario apre il link, il messaggio viene decifrato localmente e subito cancellato dal server.</p></div>
    </div>

    <div class="cta-banner">
      <h2>Provalo adesso</h2>
      <p>Nessuna registrazione. Gratis.</p>
      <a href="/app" class="btn btn-primary" id="howCtaBtn">🔒 Crea un messaggio</a>
    </div>
  `);

  document.getElementById('howCtaBtn').addEventListener('click', e => { e.preventDefault(); history.pushState({}, '', '/app'); app.classList.remove('wide'); pageCreate(); });
}

function pagePricing() {
  app.classList.add('wide');

  // Regola: una voce spuntata deve corrispondere a un task `done` nel piano.
  // Tutto il resto porta `soon: true` — la roadmap si può mostrare, il presente
  // non si può vendere per quello che non è.
  const plans = [
    {
      name: 'Free', price: '0', period: '', badge: '', desc: 'Per uso personale occasionale.',
      features: [
        { ok: true,  text: '10 messaggi al giorno' },
        { ok: true,  text: 'Durata del link: 1, 6 o 24 ore' },
        { ok: true,  text: 'Stessa cifratura di tutti i piani' },
        { ok: false, text: 'Durata del link fino a 7 giorni' },
        { ok: false, text: 'Più destinatari per messaggio' },
      ],
      cta: { label: 'Inizia gratis', href: '/app' }, featured: false,
    },
    {
      name: 'Pro', price: '4', period: '/mese', badge: '⭐ Più popolare', desc: 'Per chi usa la piattaforma regolarmente.',
      features: [
        { ok: true, text: 'Messaggi illimitati' },
        { ok: true, text: 'Durata del link: 1h, 6h, 24h, 3 o 7 giorni' },
        { ok: true, text: 'Fino a 5 destinatari, un link a testa' },
        { ok: true, text: 'Cifratura XChaCha20' },
        { ok: true, text: 'Nessuna pubblicità, nessuna attesa per chi legge' },
        { soon: true, text: 'Notifica di lettura' },
      ],
      cta: { label: 'Richiedi il piano Pro', upgrade: true }, featured: true,
    },
    {
      // Nessun prezzo esposto: quello che distingue questo piano — dominio
      // proprio, marchio al posto dello sponsor, registro degli accessi — non
      // esiste ancora, e un numero accanto a una casella vuota e' una promessa.
      // Torna quando c'e' qualcosa da vendere.
      name: 'Business', price: null, period: '', badge: '', desc: 'Per team e organizzazioni.',
      // Elenco pieno, senza "in arrivo", ma sotto una premessa che lo rende
      // vero: questo piano si costruisce sul cliente, quindi "puo' includere"
      // descrive l'ambito di una trattativa, non funzioni gia' a scaffale.
      // Le stesse voci con la spunta e senza premessa sarebbero una bugia, e
      // la pagherebbe il primo contatto commerciale vero.
      intro: 'Si costruisce insieme a te. Può includere:',
      features: [
        { ok: true, text: 'Tutto di Pro' },
        { ok: true, text: 'Dominio e marchio propri' },
        { ok: true, text: 'API REST con chiavi per servizio' },
        { ok: true, text: 'Webhook alla lettura' },
        { ok: true, text: 'Registro degli accessi' },
      ],
      cta: { label: 'Parliamone', invite: true }, featured: false,
    },
  ];

  const cards = plans.map(p => `
    <div class="pricing-card${p.featured ? ' featured' : ''}">
      ${p.badge ? `<div class="plan-badge">${p.badge}</div>` : ''}
      <div class="plan-name">${p.name}</div>
      <div class="plan-price${p.price ? '' : ' plan-price-quote'}">${p.price
        ? `<sup>€</sup>${p.price}<sub>${p.period}</sub>`
        : 'Su richiesta'}</div>
      <div class="plan-desc">${p.desc}</div>
      ${p.intro ? `<p class="plan-intro">${p.intro}</p>` : ''}
      <ul class="plan-features">
        ${p.features.filter(f => !f.soon).map(f =>
          `<li${f.ok ? '' : ' class="muted-item"'}><span class="${f.ok ? 'check' : 'cross'}">${f.ok ? '✓' : '✕'}</span>${f.text}</li>`).join('')}
      </ul>
      ${/* Fuori dall'elenco, non dentro: una riga di spunte a meta' fa sembrare
            il piano incompleto, mentre cio' che non c'e' ancora va detto una
            volta sola e senza travestirlo da funzione. */''}
      ${p.features.some(f => f.soon)
        ? `<p class="plan-soon">In arrivo: ${p.features.filter(f => f.soon)
             .map(f => f.text).join(' · ')}</p>`
        : ''}
      ${p.cta.upgrade
        ? (session
            ? `<button class="btn btn-primary" style="width:100%" id="upgradeCta">${p.cta.label}</button>`
            : `<a class="btn btn-primary" style="width:100%" href="/registrati">${p.cta.label}</a>`)
        : p.cta.invite
        ? `<a class="btn btn-invite" style="width:100%"
             href="mailto:info@monotask.it?subject=${encodeURIComponent('Piano ' + p.name)}">
             ${p.cta.label}
           </a>`
        : `<a class="btn ${p.featured ? 'btn-primary' : 'btn-ghost'}" style="width:100%"
             href="${p.cta.href}">${p.cta.label}</a>`}
    </div>
  `).join('');

  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">Prezzi</div>
      <h1>Semplice e trasparente<span>. Nessuna sorpresa.</span></h1>
    </section>
    <div class="pricing-grid">${cards}</div>
    <p class="pricing-note">Prezzi al netto dell'IVA: per un privato, 4 € sono
    <strong>4,88 € IVA inclusa</strong>. Fatturazione elettronica inclusa. Le voci
    indicate come <em>in arrivo</em> non sono comprese finché non saranno disponibili.</p>
    <div class="notice notice-info" id="upgradeMsg" role="status" hidden style="margin-top:1.2rem"></div>
  `);

  document.getElementById('upgradeCta')?.addEventListener('click', e => runUpgrade(e.currentTarget,
    document.getElementById('upgradeMsg')));
}

// Shared by /pricing and /account: the same request, reported the same way.
async function runUpgrade(btn, msg) {
  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = '⏳ Invio…';
  const ok = await requestUpgrade();
  msg.hidden = false;
  if (ok === 'already') {
    msg.className = 'notice notice-info';
    msg.innerHTML = 'La tua richiesta è già arrivata: ti scriviamo a <strong>'
      + escapeHtml(session?.email ?? '') + '</strong>. Se entro un giorno non hai '
      + 'notizie, puoi ripeterla da qui.';
    btn.textContent = 'Richiesta inviata';
    return;
  }
  if (ok) {
    msg.className = 'notice notice-info';
    msg.innerHTML = 'Richiesta inviata. Ti scriviamo a <strong>'
      + escapeHtml(session?.email ?? '') + '</strong> con i dettagli per l\'attivazione. '
      + 'Se entro un giorno non hai notizie, puoi ripeterla da qui.';
    btn.textContent = 'Richiesta inviata';
    return;
  }
  msg.className = 'notice notice-warn';
  msg.innerHTML = 'Non siamo riusciti a inviare la richiesta. '
    + 'Scrivici a <a href="mailto:info@monotask.it">info@monotask.it</a>.';
  btn.disabled = false;
  btn.textContent = label;
}

function pageFaq() {
  app.classList.add('wide');

  const items = [
    { q: 'Il server può leggere i miei messaggi?', a: 'No. La cifratura avviene nel browser prima dell\'invio. Il server riceve e archivia solo ciphertext — dati illeggibili senza la chiave, che non lascia mai il tuo dispositivo.' },
    { q: 'Cosa succede dopo la prima lettura?', a: 'Il messaggio viene cancellato dai nostri server: il link smette di funzionare e il contenuto non è più recuperabile, nemmeno da noi. Per una ventina di secondi dopo l\'apertura il destinatario può ricaricare la pagina, nel caso la rete sia caduta prima che il messaggio arrivasse a schermo; passati quelli, il messaggio non esiste più. È il motivo per cui una connessione instabile non ti fa perdere il contenuto.' },
    { q: 'Cosa succede se sbaglio destinatario?', a: 'Finché il messaggio non è stato letto puoi revocarlo: nella pagina di conferma trovi il pulsante per annullare il link, e il contenuto viene cancellato subito. Se invece è già stato letto, non c\'è modo di recuperarlo.' },
    { q: 'E se il destinatario non lo legge?', a: 'Ogni messaggio ha una scadenza che scegli tu al momento della creazione. Passata quella, viene eliminato automaticamente anche se nessuno l\'ha aperto.' },
    { q: 'Dove sono archiviati i dati?', a: 'Su server in Europa, nel datacenter di Parigi. Nessun dato transita o viene archiviato fuori dall\'Unione Europea.' },
    { q: 'Serve un account per usarlo?', a: 'No. Il piano gratuito è completamente anonimo e non chiede email. Un account servirà solo per le funzionalità a pagamento.' },
    { q: 'Che algoritmo di cifratura usate?', a: 'XChaCha20-Poly1305 tramite libsodium, la stessa libreria crittografica usata da Signal e WireGuard. La copia della libreria è servita dai nostri server, senza dipendere da CDN esterni.' },
    { q: 'Cosa non potete garantire?', a: 'Chiunque abbia il link completo può leggere il messaggio: proteggilo come proteggeresti la password che contiene. Non possiamo impedire a chi lo riceve di farne uno screenshot o di copiarlo altrove, e non possiamo sapere se il link è finito nelle mani sbagliate prima della lettura.' },
  ];

  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">FAQ</div>
      <h1>Domande frequenti</h1>
    </section>
    <div class="card" style="padding:0">
      <div class="faq-list">
        ${items.map((item, i) => `
          <div class="faq-item">
            <button class="faq-q" data-i="${i}" aria-expanded="false" aria-controls="faq-a-${i}"><span>${item.q}</span><span class="arrow" aria-hidden="true">▼</span></button>
            <div class="faq-a" id="faq-a-${i}">${item.a}</div>
          </div>`).join('')}
      </div>
    </div>
  `);

  document.querySelectorAll('.faq-q').forEach(btn => {
    btn.addEventListener('click', () => {
      const a = document.getElementById('faq-a-' + btn.dataset.i);
      const open = !btn.classList.contains('open');
      btn.classList.toggle('open', open);
      a.classList.toggle('open', open);
      // Without this the state is conveyed by the rotating arrow alone, which a
      // screen reader cannot see.
      btn.setAttribute('aria-expanded', String(open));
    });
  });
}

function pageAdvertise() {
  app.classList.add('wide');
  const mail = 'mailto:info@monotask.it?subject='
    + encodeURIComponent('Sponsorizzazione su solounavolta.it');
  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">Sponsorizzazioni</div>
      <h1>Uno spazio solo tuo<span>, davanti a un'attenzione reale</span></h1>
    </section>

    <div class="feature-grid">
      <div class="feature-card"><div class="icon">🖼️</div><h3>Schermo intero, un brand alla volta</h3>
        <p>Nessun banner in mezzo ad altri banner: chi apre un messaggio vede la tua immagine
        occupare l'intera pagina, da sola.</p></div>
      <div class="feature-card"><div class="icon">👁️</div><h3>Attenzione, non passaggio</h3>
        <p>Prima di leggere si attendono cinque secondi. È uno dei rari momenti in cui una
        persona guarda davvero lo schermo invece di scorrere.</p></div>
      <div class="feature-card"><div class="icon">🛡️</div><h3>Nessuno script, nessun tracciamento</h3>
        <p>Ci mandi un'immagine e un link, li serviamo noi. Non c'è codice di terze parti,
        quindi nessuno profila chi visita il sito — noi compresi.</p></div>
    </div>

    <div class="card">
      <h2>Ecco come lo vede chi riceve un messaggio</h2>
      <p>Non è una fotografia: è la schermata vera, riprodotta qui in piccolo con gli stessi
      elementi. Al posto dell'inserzionista di turno trovi i segnaposto del tuo materiale.</p>

      <div class="ad-preview" role="img"
           aria-label="Anteprima: la tua immagine occupa l'intera pagina, con a lato il
                       riquadro del messaggio e la scritta Sponsorizzato da il tuo marchio">
        <div class="ad-preview-bg"><span>LA TUA IMMAGINE<br><small>1200 × 900</small></span></div>
        <div class="ad-preview-panel">
          <h3>Stai per leggere un messaggio segreto</h3>
          <p>Potrai leggerlo <strong>una sola volta</strong>. Dopo sarà distrutto
          definitivamente.</p>
          <div class="ad-preview-cd">5</div>
          <div class="ad-preview-btn">🔓 Leggi il messaggio</div>
          <p class="ad-preview-sponsor">Sponsorizzato da <strong>il tuo marchio</strong></p>
        </div>
      </div>
      <p class="ad-preview-note">L'immagine occupa tutto lo sfondo e il conto alla rovescia
      dura cinque secondi: è il tempo in cui chi guarda non può fare altro.</p>

      <h2>Come funziona</h2>
      <p>Ci invii una <strong>immagine 1200×900</strong> e l'indirizzo di destinazione. La
      pubblichiamo noi. Puoi indicare una data di inizio e di fine, e se sono presenti più
      inserzionisti la rotazione è proporzionale alla quota concordata.</p>

      <h2>Cosa non accettiamo</h2>
      <p>Nessuno script di terze parti, nessun pixel di tracciamento, nessun iframe. Non è
      una preferenza: la pagina che ospita l'annuncio contiene nell'indirizzo la chiave per
      decifrare un messaggio, quindi un codice esterno potrebbe leggerla. Per questo lo
      spazio è vendibile solo come immagine più link — ed è anche il motivo per cui il tuo
      annuncio non compete con nessun altro.</p>
      <p>Restano fuori anche annunci ingannevoli, contenuti per adulti, gioco d'azzardo e
      tutto ciò che finge di essere un messaggio o un avviso di sistema.</p>

      <h2>Quanto costa</h2>
      <p>Dipende dal volume del periodo e dalla quota di impression. Scrivici indicando che
      tipo di pubblico cerchi: rispondiamo con i numeri reali del traffico, senza stime
      gonfiate.</p>

      <div style="margin-top:1.5rem;text-align:center">
        <a class="btn btn-primary" href="${mail}">Richiedi uno spazio</a>
      </div>
    </div>

    <p class="secondary-cta">
      Oppure scrivi direttamente a <a href="mailto:info@monotask.it">info@monotask.it</a>
    </p>
  `);
}

function pageTerms() {
  app.classList.add('wide');
  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">Termini di servizio</div>
      <h1>Le regole, in italiano<span> comprensibile</span></h1>
    </section>

    <div class="card">
      <p><strong>In breve:</strong> il servizio è fornito così com'è, non garantiamo che un
      messaggio arrivi o resti disponibile, non possiamo recuperarlo, e blocchiamo chi lo usa
      per truffare o delinquere. Sotto la versione completa.</p>

      <h2>1. Chi siamo</h2>
      <p>Il servizio è offerto da <strong>Monotask S.r.l.</strong>, piazza Raffaele Laudati 8,
      70022 Altamura (BA), P.IVA 08278810729 — di seguito "noi".
      Contatto: <a href="mailto:info@monotask.it">info@monotask.it</a>.</p>

      <h2>2. Cosa fa il servizio</h2>
      <p>Permette di cifrare un testo nel proprio browser e di condividerlo tramite un link
      che funziona una sola volta. Dopo la lettura, o alla scadenza scelta, il contenuto
      viene eliminato dai nostri server.</p>
      <p>Il piano gratuito non richiede registrazione e ha limiti di utilizzo indicati nella
      <a href="/pricing">pagina prezzi</a>. Possiamo modificarli, dandone conto su quella
      pagina.</p>

      <h2>3. Cosa NON possiamo fare</h2>
      <p>Questo punto è la conseguenza tecnica di come è costruito il servizio, non una
      scelta commerciale. La chiave di cifratura resta nel link e non ci raggiunge mai,
      quindi <strong>non possiamo</strong>:</p>
      <ul>
        <li>leggere il contenuto di un messaggio;</li>
        <li>recuperare un messaggio letto, scaduto o revocato;</li>
        <li>dirti chi ha letto un messaggio o da dove;</li>
        <li>rigenerare un link perso o incompleto.</li>
      </ul>
      <p>Se un messaggio è importante, conservane una copia prima di inviarlo.</p>

      <h2>4. Uso consentito</h2>
      <p>Il servizio va usato nel rispetto della legge. In particolare è vietato usarlo per
      inviare comunicazioni ingannevoli o di phishing, diffondere contenuti illeciti,
      violare diritti altrui, aggirare i limiti tecnici o automatizzarne l'uso su larga
      scala senza accordo.</p>
      <p><strong>Possiamo bloccare l'accesso immediatamente e senza preavviso</strong> a chi
      lo usa in questo modo, e chiudere l'account di chi ne ha uno. Va detto onestamente che
      per chi usa il servizio in forma anonima il blocco avviene per indirizzo IP: è uno
      strumento grossolano e facilmente aggirabile. Non è un sistema di controllo, è la
      possibilità di interrompere un abuso quando lo rileviamo o ci viene segnalato.</p>
      <p>Non ispezioniamo i contenuti — tecnicamente non potremmo — quindi la responsabilità
      di ciò che invii è interamente tua.</p>

      <h2>5. Disponibilità e responsabilità</h2>
      <p>Il servizio è fornito <em>"così com'è"</em>. Non garantiamo che sia sempre
      raggiungibile, privo di errori, né che un messaggio venga consegnato o letto.</p>
      <p>Nei limiti consentiti dalla legge, la nostra responsabilità complessiva è limitata
      a quanto ci hai effettivamente pagato nei dodici mesi precedenti l'evento — quindi
      <strong>pari a zero se usi il piano gratuito</strong>.</p>
      <p>Questa limitazione <strong>non si applica</strong> ai casi di dolo o colpa grave, né
      ai diritti che la legge riconosce ai consumatori e che non possono essere esclusi.</p>

      <h2>6. Account e piani a pagamento</h2>
      <p>L'account è facoltativo e gratuito: si apre con un link inviato al tuo indirizzo
      email, senza password. Serve per limiti più alti e per le funzioni a pagamento. Puoi
      eliminarlo da solo in qualsiasi momento dalla pagina del tuo account; i messaggi che
      hai già inviato restano validi fino alla loro scadenza.</p>
      <p><strong>L'incasso dei pagamenti non è ancora attivo.</strong> Il piano Pro viene
      oggi attivato manualmente su richiesta, con fatturazione separata. Quando sarà
      disponibile un sistema di pagamento online, prezzi, durata, rinnovo e diritto di
      recesso saranno indicati prima dell'acquisto e questi termini verranno aggiornati.</p>

      <h2>7. Trattamento dei dati</h2>
      <p>Come trattiamo i dati è descritto nell'<a href="/privacy">informativa privacy</a>,
      che è parte integrante di questi termini.</p>

      <h2>8. Modifiche</h2>
      <p>Possiamo aggiornare questi termini. Le modifiche sostanziali saranno pubblicate su
      questa pagina con una nuova data. Continuare a usare il servizio dopo la pubblicazione
      significa accettarle; se non le accetti, smetti di usarlo.</p>

      <h2>9. Legge applicabile e foro</h2>
      <p>Si applica la <strong>legge italiana</strong>. Per le controversie con utenti
      professionali il foro competente è quello di <strong>Bari</strong>.</p>
      <p>Se sei un <strong>consumatore</strong>, resta ferma la competenza del giudice del
      luogo in cui risiedi o hai il domicilio, che la legge non consente di derogare, e
      restano impregiudicati tutti i diritti che il Codice del consumo ti riconosce.</p>

      <p style="color:var(--muted);margin-top:2rem">Ultimo aggiornamento: 11 settembre 2026.</p>
    </div>
  `);
}

function pageVerify() {
  render(`
    ${pageTitle('Verificalo tu stesso')}
    <div class="card">
      <p>Diciamo che non possiamo leggere i tuoi messaggi. È una promessa comoda da fare
      e impossibile da smentire a parole, quindi qui trovi come <strong>controllarla di
      persona</strong>, senza fidarti di noi e senza installare niente.</p>

      <h2>La prova che non richiede fiducia</h2>
      <p>Serve un browser e due minuti.</p>
      <ol>
        <li>Apri <a href="/app">la pagina di creazione</a> e premi <kbd>F12</kbd>
        (su Mac <kbd>⌥⌘I</kbd>) per aprire gli strumenti di sviluppo. Vai su
        <strong>Rete</strong>.</li>
        <li>Scrivi un messaggio riconoscibile — per esempio <code>ciao mamma</code> — e
        genera il link.</li>
        <li>Nell'elenco delle richieste cerca <code>message</code> e guarda il
        <strong>corpo inviato</strong>. Trovi un campo <code>ciphertext</code> con
        caratteri illeggibili. <strong>Cerca <code>ciao mamma</code>: non c'è.</strong>
        Non è mai partito dal tuo dispositivo.</li>
        <li>Guarda ora il link generato. Dopo il simbolo <code>#</code> c'è la chiave.
        Torna su <strong>Rete</strong> e cerca quella stringa in una qualunque richiesta:
        <strong>non compare da nessuna parte</strong>. È una regola del web, non una
        nostra gentilezza — i browser non inviano al server ciò che sta dopo il
        <code>#</code>.</li>
      </ol>
      <p>Questo è il punto. Se il testo parte già illeggibile e la chiave non parte
      affatto, <strong>non conta che server abbiamo</strong>: la promessa regge da sé.</p>

      <h2>Se vuoi leggere il codice</h2>
      <p>Il programma che fa tutto questo è già sul tuo dispositivo: il tuo browser lo ha
      scaricato aprendo questa pagina. Pubblichiamo anche le <em>source map</em>, così
      negli strumenti di sviluppo, sotto <strong>Sorgenti</strong>, lo leggi com'è stato
      scritto invece che compresso. La parte che riguarda la cifratura è un file solo,
      <code>crypto.js</code>, ed è lungo <strong>42 righe</strong>.</p>

      <h2>Quello che questa pagina NON dimostra</h2>
      <p>Per onestà, i limiti — che valgono per qualunque servizio di questo tipo, incluso
      chi pubblica tutto il proprio codice:</p>
      <ul>
        <li><strong>Non dimostra cosa gira sui nostri server.</strong> Nessuno può
        dimostrarlo, e chi pubblica il codice del server non fa eccezione: tra quel codice
        e il processo in esecuzione non c'è nessuna prova. È il motivo per cui abbiamo
        costruito il prodotto in modo che <em>non importi</em>.</li>
        <li><strong>Non ti protegge da noi che un domani cambiamo il programma.</strong>
        Il codice che il tuo browser scarica lo scegliamo noi, ogni volta. Se un giorno
        spedissimo una versione che invia anche la chiave, la verifica di oggi non lo
        impedirebbe — potresti però accorgertene rifacendola, ed è un'altra ragione per
        cui le istruzioni qui sopra sono pubbliche.</li>
        <li><strong>Non ti protegge da chi riceve il messaggio.</strong> Chi lo apre lo
        legge, e può copiarlo. Nessuna cifratura può impedirlo.</li>
      </ul>

      <h2>Le due cose che sappiamo</h2>
      <p>Perché la trasparenza vale solo se comprende anche ciò che è scomodo:</p>
      <ul>
        <li><strong>Se apri un account, il tuo indirizzo email lo leggiamo</strong> —
        serve a mandarti il link di accesso. È cifrato a riposo, ma la chiave è del
        fornitore dell'infrastruttura, quindi quella cifratura protegge dai supporti
        fisici, non da noi.</li>
        <li><strong>Sappiamo quando un messaggio viene aperto</strong>, per i pochi
        secondi che servono a distruggerlo. Perché e per quanto è spiegato
        nell'<a href="/privacy">informativa</a>, punto 3a.</li>
      </ul>
      <p>Il contenuto dei messaggi resta l'unica cosa su cui non abbiamo scelta: quello
      non possiamo leggerlo nemmeno volendo, ed è esattamente ciò che hai appena
      verificato.</p>
    </div>
  `);
}

function pageContacts() {
  render(`
    ${pageTitle('Contatti')}
    <div class="card">
      <h2>Scrivici</h2>
      <p>Per qualsiasi richiesta — supporto, privacy, sponsorizzazioni o piani a pagamento —
      l'indirizzo è <a href="mailto:info@monotask.it">info@monotask.it</a>.</p>
      <p>Rispondiamo in italiano e in inglese.</p>

      <h2>Titolare del servizio</h2>
      <p><strong>Monotask S.r.l.</strong><br>
      Piazza Raffaele Laudati 8<br>
      70022 Altamura (BA), Italia<br>
      P.IVA 08278810729</p>

      <h2>Una cosa che non possiamo fare</h2>
      <p>Non possiamo recuperare un messaggio, né dirti chi lo ha letto o da dove. Non è una
      politica aziendale: i messaggi sono cifrati sul dispositivo di chi li scrive e vengono
      distrutti alla lettura, quindi quell'informazione non esiste da nessuna parte.
      Se hai bisogno di annullare un invio, usa il pulsante di revoca nella pagina che ti
      mostra il link, finché il messaggio non è stato aperto.</p>
    </div>
  `);
}

function pageNotFound() {
  render(`
    <div class="card">
      <div class="destroyed-notice">
        <div class="icon">🔍</div>
        <h2>Pagina non trovata</h2>
        <p>L'indirizzo che hai aperto non esiste.</p>
        <div style="margin-top:1.5rem"><a href="/" class="btn btn-primary">Torna alla home</a></div>
      </div>
    </div>`);
}

// Written from what the system actually does, verified in the code: only the
// ciphertext, its expiry and the revocation token are stored per message; the
// only per-visitor datum is a salted hash of the IP used for the daily cap.
function pagePrivacy() {
  app.classList.add('wide');
  render(`
    <section class="hero" style="padding-bottom:2rem">
      <div class="hero-eyebrow">Informativa privacy</div>
      <h1>Cosa trattiamo<span>, e cosa non possiamo trattare</span></h1>
    </section>

    <div class="card">
      <h2>1. Titolare del trattamento</h2>
      <p>Titolare del trattamento è <strong>Monotask S.r.l.</strong>, con sede in
      piazza Raffaele Laudati 8, 70022 Altamura (BA), P.IVA 08278810729.<br>
      Per qualsiasi richiesta relativa ai tuoi dati scrivi a
      <a href="mailto:info@monotask.it">info@monotask.it</a>.</p>

      <h2>2. Il punto di partenza: non vediamo i tuoi messaggi</h2>
      <p>Il contenuto che scrivi viene cifrato <strong>dentro il tuo browser</strong>, prima
      di essere trasmesso. La chiave di cifratura resta nella parte dell'indirizzo dopo il
      simbolo <code>#</code>, che per come funziona il web <strong>non viene mai inviata al
      server</strong>. Sui nostri sistemi esiste solo una sequenza di caratteri illeggibile.</p>
      <p>Non si tratta di una promessa organizzativa ma di un limite tecnico: anche volendo,
      anche se ci venisse ordinato, non potremmo leggere il contenuto di un messaggio, perché
      non possediamo la chiave.</p>

      <h2>3. Quali dati trattiamo</h2>
      <h3>a) Contenuto cifrato dei messaggi</h3>
      <p>Per ogni messaggio conserviamo esclusivamente: il testo cifrato, la data di scadenza
      e un codice casuale che consente a chi lo ha creato di revocarlo. Non registriamo chi
      lo ha inviato, chi lo ha letto, da dove, né quando è stato creato.</p>
      <p>Alla prima apertura il messaggio viene inoltre marcato con <strong>l'orario di
      quell'apertura</strong>. È un dato tecnico e serve a una cosa sola: calcolare i pochi
      secondi entro cui il destinatario può ricaricare la pagina se la rete cade, e
      distruggere il messaggio appena scaduti. Non lo colleghiamo a nessuna persona, non lo
      consultiamo e non lo usiamo per dirti se un messaggio è stato letto.</p>
      <p>Base giuridica: <strong>legittimo interesse</strong> (art. 6.1.f GDPR) a far
      funzionare la distruzione dopo la lettura, che è la funzione stessa del servizio.
      Conservazione: il marcatore vive quanto il messaggio che lo porta, e una procedura
      automatica elimina <strong>ogni ora</strong> i messaggi il cui tempo di ricarica è
      scaduto, anche se nessuno li ha più aperti.</p>

      <h3>b) Un identificatore derivato dal tuo indirizzo IP</h3>
      <p>Per impedire abusi e applicare il limite di messaggi giornalieri del piano gratuito,
      calcoliamo un <strong>hash crittografico del tuo indirizzo IP</strong> con una chiave
      segreta, e conserviamo solo quello insieme a un contatore. <strong>L'indirizzo IP in
      chiaro non viene mai scritto</strong> e l'hash non è riconducibile all'indirizzo
      originale senza la chiave segreta.</p>
      <p>Base giuridica: legittimo interesse (art. 6.1.f GDPR) a prevenire l'uso abusivo di un
      servizio gratuito. Conservazione: <strong>2 giorni</strong>.</p>

      <h3>c) I dati del tuo account, se ne apri uno</h3>
      <p>L'account è <strong>facoltativo</strong>: serve per i limiti più alti e per le
      funzioni a pagamento. Se lo apri, conserviamo il tuo <strong>indirizzo email</strong>,
      il piano attivo e la data di creazione. Nient'altro: né nome, né azienda, né
      cronologia dei messaggi inviati.</p>
      <p>L'indirizzo è conservato <strong>in chiaro</strong> dentro il record, perché
      dobbiamo poterti scrivere per farti accedere. Il nome del file che lo contiene è
      invece un hash con chiave segreta dell'indirizzo: serve a impedire che l'elenco dei
      file diventi leggibile come una rubrica.</p>
      <p>Il record è inoltre <strong>cifrato a riposo</strong> sullo storage. Questo lo
      protegge da chi arrivasse ai supporti fisici, <strong>non da noi</strong>: la chiave
      è gestita dal fornitore dell'infrastruttura e noi il tuo indirizzo lo leggiamo, perché
      serve a mandarti il link di accesso. È l'opposto di quanto avviene per il contenuto
      dei messaggi, che non possiamo leggere in nessun caso.</p>
      <p>Per poter rimediare a un errore, quando il record cambia (per esempio quando
      attiviamo un piano) ne conserviamo la <strong>versione precedente per 30 giorni</strong>,
      poi viene eliminata automaticamente. Se elimini l'account, invece, cancelliamo
      subito anche tutte le versioni precedenti.</p>
      <p>Base giuridica: esecuzione del contratto (art. 6.1.b GDPR). Conservazione:
      <strong>finché non elimini l'account</strong>, cosa che puoi fare da solo e in
      qualsiasi momento dalla pagina del tuo account.</p>

      <h3>d) I link di accesso</h3>
      <p>Non usiamo password. Quando chiedi di accedere ti inviamo un link monouso; sui
      nostri sistemi non finisce il link, ma solo la sua <strong>impronta crittografica</strong>
      insieme al tuo indirizzo. Vale <strong>15 minuti</strong> e viene distrutto
      all'uso; quelli mai usati vengono comunque eliminati <strong>entro un giorno</strong>.</p>
      <p>La sessione che ottieni dopo l'accesso è un cookie tecnico, necessario a tenerti
      collegato, che dura <strong>30 giorni</strong>. Non serve a tracciarti e non richiede
      consenso.</p>
      <p>Base giuridica: <strong>esecuzione del contratto</strong> (art. 6.1.b GDPR) —
      senza questi dati non potremmo darti accesso al tuo account.</p>

      <h3>e) Log tecnici dell'infrastruttura</h3>
      <p>Il fornitore che ospita il servizio può registrare log tecnici di connessione,
      che possono includere l'indirizzo IP, per finalità di sicurezza e funzionamento della
      rete. Sono trattati dal fornitore in qualità di responsabile del trattamento, secondo
      i propri tempi di conservazione. Noi non li consultiamo né li utilizziamo.</p>
      <p>Base giuridica: <strong>legittimo interesse</strong> (art. 6.1.f GDPR) alla
      sicurezza e al corretto funzionamento della rete.</p>

      <h3>f) Preferenza di aspetto</h3>
      <p>Se scegli il tema chiaro o scuro, la preferenza viene salvata
      <strong>nel tuo browser</strong> (<code>localStorage</code>) e non raggiunge mai i
      nostri server. Non è un cookie di tracciamento e non richiede consenso.</p>

      <h2>4. Cosa NON facciamo</h2>
      <ul>
        <li>Non chiediamo email, nome o alcun dato di registrazione per usare il
        servizio gratuito, che è e resterà utilizzabile in forma anonima. L'account
        esiste, ma è facoltativo: senza, il servizio funziona per intero.</li>
        <li>Non colleghiamo i messaggi a chi li ha creati, nemmeno se ha un account.
        Non esiste da nessuna parte un elenco di cosa hai inviato.</li>
        <li>Non usiamo cookie di profilazione né strumenti di analisi statistica.</li>
        <li>Non carichiamo risorse da servizi esterni: nessun font, script o immagine di
        terze parti, quindi nessun soggetto esterno vede la tua visita.</li>
        <li>Non vendiamo, cediamo né condividiamo dati con terzi per finalità commerciali.</li>
        <li>Non profiliamo gli utenti e non prendiamo decisioni automatizzate sul loro conto.</li>
      </ul>

      <h2>5. Per quanto tempo conserviamo i dati</h2>
      <ul>
        <li><strong>Messaggi letti</strong>: eliminati subito dopo la lettura, passati i
        pochi secondi di tolleranza descritti al punto 3a. Se il destinatario chiude la
        pagina prima che la conferma di distruzione ci arrivi, ci pensa la procedura
        automatica oraria.</li>
        <li><strong>Messaggi mai letti</strong>: eliminati alla scadenza scelta da chi li ha
        creati &mdash; al massimo 24 ore senza account o con account gratuito, 7 giorni
        con il piano Pro.</li>
        <li><strong>Limite di sicurezza</strong>: una regola automatica dello storage elimina
        comunque ogni contenuto dopo 8 giorni, qualunque cosa accada.</li>
        <li><strong>Contatori antiabuso</strong>: 2 giorni.</li>
        <li><strong>Link di accesso</strong>: validi 15 minuti, distrutti all'uso ed
        eliminati comunque entro un giorno.</li>
        <li><strong>Account</strong>: finché non lo elimini tu. Non ha scadenza
        automatica, perché sparire senza preavviso sarebbe peggio che restare.</li>
      </ul>

      <h2>6. Dove sono i dati</h2>
      <p>Tutti i dati sono ospitati su infrastruttura <strong>Scaleway</strong>, nel
      datacenter di <strong>Parigi (Francia)</strong>. Non avvengono trasferimenti fuori
      dallo Spazio Economico Europeo. Scaleway agisce come responsabile del trattamento.</p>

      <h2>7. I tuoi diritti</h2>
      <p>Puoi chiedere accesso, rettifica, cancellazione, limitazione, opposizione al
      trattamento e <strong>portabilità</strong> dei dati (art. 20 GDPR), e proporre
      reclamo al Garante per la protezione dei dati personali.</p>
      <p><strong>Se hai un account</strong>, questi diritti sono pienamente esercitabili:
      sappiamo quale record ti appartiene. Puoi <strong>eliminare l'account da solo</strong>,
      in qualsiasi momento, dal pulsante nella pagina del tuo account: la cancellazione è
      immediata e definitiva. I messaggi che hai già inviato <strong>non</strong> vengono
      distrutti, perché non sono collegati all'account e i loro link sono già nelle mani dei
      destinatari; scadono da soli come previsto.</p>
      <p><strong>Se invece usi il servizio in forma anonima</strong>, vale un limite di cui
      devi essere consapevole: non raccogliamo alcun dato identificativo, quindi non siamo in
      grado di collegare un messaggio o un contatore a una persona. Non possiamo dare seguito
      a una richiesta di accesso o cancellazione riferita a dati specifici, perché non abbiamo
      modo di verificare a chi appartengano (art. 11 GDPR). Se hai creato un messaggio e vuoi
      eliminarlo, puoi farlo <strong>subito e da solo</strong> con il pulsante di revoca nella
      pagina che ti mostra il link: è più rapido ed efficace di qualsiasi richiesta a noi.</p>

      <h2>8. Account e piani a pagamento</h2>
      <p>Gli account sono <strong>attivi</strong>. Due cose che restano vere:</p>
      <ul>
        <li><strong>Il servizio gratuito resta utilizzabile senza account</strong>, in forma
        anonima e con tutte le funzioni essenziali. L'account alza i limiti e sblocca le
        funzioni a pagamento; non è un pedaggio.</li>
        <li><strong>Avere un account non ci permette comunque di leggere i tuoi
        messaggi.</strong> La cifratura è la stessa: la chiave continua a non raggiungerci.
        L'account identifica chi sei, non cosa scrivi.</li>
      </ul>
      <p><strong>I pagamenti non sono ancora attivi.</strong> Oggi il piano Pro viene
      attivato manualmente su richiesta e non trattiamo nessun dato di pagamento: non
      esiste un sistema di incasso collegato al sito. Quando ci sarà, questa informativa
      verrà aggiornata indicando chi tratta i dati di pagamento e con quale ruolo.</p>
      <p><strong>Pubblicità.</strong> La pagina su cui si legge un messaggio ospita uno
      spazio sponsorizzato con un collegamento verso il sito dell'inserzionista. L'immagine
      è servita dal nostro dominio: non carichiamo nulla da circuiti pubblicitari esterni,
      non usiamo cookie di profilazione e l'inserzionista non riceve alcun dato su di te,
      salvo quello che il tuo browser invia da sé se scegli di fare clic sul collegamento.</p>

      <h2>9. Modifiche</h2>
      <p>Ogni modifica sostanziale a questa informativa sarà pubblicata su questa pagina con
      una nuova data di aggiornamento.</p>

      <p style="color:var(--muted);margin-top:2rem">Ultimo aggiornamento: 11 settembre 2026.</p>
    </div>
  `);
}

// ── Router ────────────────────────────────────────────────────────────────────

// Where the magic link lands. It has to show something that visibly changed,
// otherwise confirming reads as having done nothing at all.
function pageAccount() {
  if (!session) { pageLogin(); return; }

  const pro = session.plan === 'pro';
  render(`
    ${pageTitle('Il tuo account', 'Sei dentro')}
    <div class="card">
      <p style="margin-top:0">Sei entrato come <strong>${escapeHtml(session.email)}</strong>.</p>
      <table class="plan-table">
        <tr><td>Piano</td><td><strong>${pro ? 'Pro' : 'Gratuito'}</strong></td></tr>
        <tr><td>Messaggi al giorno</td><td>${session.daily_limit === 0
          ? 'senza limite' : session.daily_limit}</td></tr>
        <tr><td>Durata massima del link</td><td>${pro ? '7 giorni' : '24 ore'}</td></tr>
        ${pro && session.pro_until ? `<tr><td>Attivo fino al</td><td>${
          new Date(session.pro_until * 1000).toLocaleDateString('it-IT',
            { day: 'numeric', month: 'long', year: 'numeric' })}</td></tr>` : ''}
      </table>
      <div class="form-row" style="margin-top:1.4rem">
        <a href="/app" class="btn ${pro ? 'btn-primary' : 'btn-ghost'}">Crea messaggio</a>
        ${pro ? '' : '<button class="btn btn-primary" id="upgradeCta">Passa a Pro</button>'}
      </div>
      ${pro ? '' : `<div class="notice notice-info" id="upgradeMsg" role="status" hidden
        style="margin-top:1.2rem"></div>`}
    </div>
    <div class="card" style="margin-top:1.4rem">
      <h2 style="font-size:1.05rem;margin-bottom:.6rem">Elimina account</h2>
      <p style="color:var(--muted);font-size:.92rem;line-height:1.55">
        Cancella subito e definitivamente il tuo indirizzo dai nostri sistemi.
        I messaggi che hai già inviato restano validi fino alla loro scadenza: non sono
        collegati all'account, e i loro link sono già nelle mani dei destinatari.</p>
      <div class="revoke-box" id="delBox" style="margin-top:1rem">
        <button class="btn btn-danger" id="delBtn">Elimina il mio account</button>
        <span class="revoke-status" id="delStatus"></span>
      </div>
    </div>
  `);

  document.getElementById('upgradeCta')?.addEventListener('click', e => runUpgrade(
    e.currentTarget, document.getElementById('upgradeMsg')));

  document.getElementById('delBtn').addEventListener('click', async () => {
    const btn = document.getElementById('delBtn');
    const status = document.getElementById('delStatus');
    if (!confirm('Eliminare l\'account? L\'operazione è immediata e non si può annullare. '
      + 'I messaggi già inviati restano validi fino alla scadenza.')) return;
    btn.disabled = true;
    btn.textContent = '⏳ Eliminazione…';
    if (await deleteAccount()) {
      session = null;
      renderNavSession();
      render(`
        ${pageTitle('Account eliminato')}
        <div class="card">
          <p style="margin-top:0">Il tuo indirizzo non è più sui nostri sistemi.</p>
          <p>Il servizio resta utilizzabile senza account. Se un giorno ne vuoi un altro,
          basta chiedere un nuovo link di accesso.</p>
          <a href="/app" class="btn btn-primary">Crea un messaggio</a>
        </div>
      `);
      return;
    }
    status.textContent = 'Non ha funzionato. Riprova, o scrivici a info@monotask.it.';
    status.className = 'revoke-status warn';
    btn.disabled = false;
    btn.textContent = 'Elimina il mio account';
  });
}

function pageLogin() {
  if (session) { pageAccount(); return; }

  const invalid = new URLSearchParams(location.search).get('auth') === 'invalid';
  render(`
    ${pageTitle('Accedi', 'Nessuna password: ti mandiamo un link')}
    <div class="card">
      ${invalid ? `<div class="notice notice-warn" role="alert" style="margin-bottom:1.2rem">
        Quel link non è più valido: scade dopo 15 minuti e funziona una volta sola.
        Chiedine un altro qui sotto.</div>` : ''}
      <label for="email">Il tuo indirizzo email</label>
      <input type="email" id="email" autocomplete="email" placeholder="tu@esempio.it">
      <button class="btn btn-primary" id="magicBtn"
              style="width:100%;margin-top:1rem">Mandami il link</button>
      <div class="notice notice-info" id="magicSent" role="status" hidden style="margin-top:1.2rem"></div>
      <p class="auth-switch">Non hai un account? <a href="/registrati">Registrati</a></p>
    </div>
  `);

  document.getElementById('magicBtn').addEventListener('click', async () => {
    const btn = document.getElementById('magicBtn');
    const email = document.getElementById('email').value.trim();
    if (!email) return;
    btn.disabled = true;
    btn.textContent = 'Invio…';
    await requestMagicLink(email);
    // Deliberately the same message whatever happened: the server answers 204
    // for an unknown address too, and saying "we don't know you" here would
    // give away who has an account. The "Registrati" link above is rendered
    // with the page and never changes in response to a submit — a line that
    // appeared only for unknown addresses would be that same oracle, moved
    // into the DOM.
    const sent = document.getElementById('magicSent');
    sent.innerHTML = `Se <strong>${escapeHtml(email)}</strong> ha un account, `
      + 'il link è in arrivo. Scade fra 15 minuti.';
    sent.hidden = false;
    btn.textContent = 'Link inviato';
  });
}

function pageRegister() {
  if (session) { pageAccount(); return; }

  render(`
    ${pageTitle('Registrati', 'Nessuna password: ti mandiamo un link')}
    <div class="card">
      <label for="email">Il tuo indirizzo email</label>
      <input type="email" id="email" autocomplete="email" placeholder="tu@esempio.it">
      <div aria-hidden="true" style="position:absolute;left:-9999px">
        <label for="website">Non compilare questo campo</label>
        <input type="text" id="website" tabindex="-1" autocomplete="off">
      </div>
      <button class="btn btn-primary" id="regBtn"
              style="width:100%;margin-top:1rem">Crea il mio account</button>
      <div class="notice notice-info" id="regSent" role="status" hidden style="margin-top:1.2rem"></div>
      <p class="auth-switch">Hai già un account? <a href="/accedi">Accedi</a></p>
    </div>
  `);

  document.getElementById('regBtn').addEventListener('click', async () => {
    const btn = document.getElementById('regBtn');
    const email = document.getElementById('email').value.trim();
    if (!email) return;
    btn.disabled = true;
    btn.textContent = 'Invio…';
    await registerAccount(email, document.getElementById('website').value);
    // Printed always, for everyone, whatever the server did — that is what keeps
    // it from becoming the oracle the 204 refuses to be.
    //
    // It does overstate one case: an address that already has an account gets no
    // mail from this route, and this line still says one went out. What catches
    // that person is the "Hai già un account? Accedi" link, which is rendered
    // with the page, sits two lines below this box, and never moves. Spelling
    // the case out here as well was tried and read as padding.
    const sent = document.getElementById('regSent');
    sent.innerHTML = `Ti abbiamo inviato una mail a <strong>${escapeHtml(email)}</strong>. `
      + 'Hai 15 minuti per confermare.';
    sent.hidden = false;
    btn.textContent = 'Link inviato';
  });
}

function route() {
  const path = location.pathname;
  const hash = location.hash.slice(1);

  app.classList.remove('wide');

  const match = path.match(/^\/m\/([a-zA-Z0-9_-]+)$/);
  if (match) {
    const token = match[1];
    // Sent from a Pro account: straight to the message, no wait and no
    // sponsor. Read from the URL rather than from the message, because the
    // interstitial runs BEFORE the message is fetched and fetching it is what
    // consumes it — see the note on `no_ads` in the api handler.
    const noAds = new URLSearchParams(location.search).get('np') === '1';
    // No fragment means the link was truncated, NOT that the message is gone:
    // showing "already read" here accuses someone of having opened it.
    if (!hash)      { pageIncompleteLink(); return; }
    if (noAds)      { pageView(token, hash); return; }
    pageInterstitial(token, hash);
    return;
  }

  if (path === '/' || path === '')   { pageHome(); return; }
  if (path === '/app')               { pageCreate(); return; }
  if (path === '/accedi')            { pageLogin(); return; }
  if (path === '/registrati')        { pageRegister(); return; }
  if (path === '/account')           { pageAccount(); return; }
  if (path === '/come-funziona')     { pageHowItWorks(); return; }
  if (path === '/pricing')           { pagePricing(); return; }
  if (path === '/faq')               { pageFaq(); return; }
  if (path === '/privacy')           { pagePrivacy(); return; }
  if (path === '/verifica')          { pageVerify(); return; }
  if (path === '/sponsorizzazioni')  { pageAdvertise(); return; }
  if (path === '/contatti')          { pageContacts(); return; }
  if (path === '/termini')           { pageTerms(); return; }

  // Restore the "link generated" screen after a refresh.
  if (path === '/inviato') {
    let saved = history.state;
    if (!saved?.sent && !saved?.link) {
      try { saved = JSON.parse(sessionStorage.getItem('lastSent') || 'null'); } catch { saved = null; }
    }
    if (saved?.sent?.length) { pageSent(saved.sent); return; }
    // A tab left open across the deploy that introduced multiple recipients
    // still holds the old single-link shape.
    if (saved?.link) { pageSent([{ link: saved.link, deleteToken: saved.deleteToken }]); return; }
    pageCreate();
    return;
  }

  // A message that was just read lands here after the URL is cleaned.
  if (path === '/letto')             { pageDestroyed(); return; }

  // Anything else is a wrong address, not a consumed message: saying "already
  // read" would suggest someone opened a secret that never existed.
  pageNotFound();
}

window.addEventListener('popstate', route);

// Intercept internal nav links (avoids full reload)
document.addEventListener('click', e => {
  const a = e.target.closest('a[href]');
  if (!a) return;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin) return;
  e.preventDefault();
  if (url.pathname !== location.pathname) {
    history.pushState({}, '', url.pathname + url.search + url.hash);
    route();
  }
});

// Boot. The session is read before the first render so the page never flashes
// a logged-out state at someone who is logged in — and so the TTL select is
// built with the right ceiling the first time.
(async () => {
  session = await getMe();
  renderNavSession();
  route();
})();
